import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AppStore } from "@/store/AppStore";
import type { SmartContextRow } from "@/domains/database/repositories/smart-context-repository";
import type { SmartViewRow } from "@/domains/database/repositories/smart-view-repository";
import type { SmartCardRow } from "@/domains/database/repositories/smart-card-repository";
import type { INoteObservable } from "@/store/note/types";
import { SmartViewKind } from "@/domains/smart/constants";
import type { Maybe } from "@/domains/common/types";
import { resolveSmartContextId } from "@/store/heads-up/SmartContextId";
import type { HeadsUpContextInfo } from "./use-context-id";

export interface HeadsUpDataSourceResult {
  contextRow: SmartContextRow | null;
  view: SmartViewRow | null;
  viewError: string | null;
  isViewLoading: boolean;
  cards: SmartCardRow[];
  isCardsLoading: boolean;
  cardsSignature: string;
  smartViewId: string | null;
  note: INoteObservable | undefined;
  noteTitle: string | null;
  noteHasPrimaryLabel: boolean;
  autoGenerationAllowed: boolean;
  errorForContext: string | null;
  hasFoundContentBefore: boolean;
  statuses: {
    isGenerating: boolean;
    isHydrating: boolean;
    isRefreshing: boolean;
    isRegenerating: boolean;
    isFindingMore: boolean;
    isOperationInFlight: boolean;
  };
  actions: {
    refreshSmartView: (options?: { isAuto?: boolean }) => void;
    regenerateSmartView: () => void;
    generateForContext: () => void;
    findMore: () => void;
    ensureNoteHydrated: (noteId: string | null | undefined) => Promise<Maybe<INoteObservable>>;
    isNoteLoading: (noteId: string) => boolean;
  };
}

export function useDataSource(
  store: AppStore,
  contextInfo: HeadsUpContextInfo
): HeadsUpDataSourceResult {
  const { contextId, activeNoteId, spaceAccountId, isDatabaseReady } = contextInfo;

  const { context } = store.database.smartContexts.useById({
    contextId: isDatabaseReady ? contextId : null,
  });

  const smartContextId =
    context?.id ??
    resolveSmartContextId({
      spaceAccountId,
      activeNoteId,
      smartViewKind: SmartViewKind.HeadsUp,
    });

  const {
    view,
    isLoading: isViewLoading,
    error: viewError,
  } = store.database.smartViews.useLatestByContextId({
    smartContextId: isDatabaseReady ? (smartContextId ?? null) : null,
  });

  const smartViewId = view?.id ?? null;

  const { cards, isLoading: isCardsLoading } = store.database.smartCards.useCardsBySmartViewId({
    smartViewId: isDatabaseReady ? smartViewId : null,
  });

  const cardsSignature = useMemo(() => {
    if (cards.length === 0) return "NONE";
    return cards.map(card => card.id).join("|");
  }, [cards]);

  const note = isDatabaseReady && activeNoteId ? store.notes.get(activeNoteId) : undefined;
  const noteTitle = note ? note.title : null;
  const noteHasPrimaryLabel = Boolean(note?.hasPrimaryLabel);

  const autoGenerationAllowed = store.headsUp.canAutoGenerateForContextId(contextId);

  const statuses = {
    isGenerating: store.headsUp.isGeneratingForContextId(contextId),
    isHydrating: store.headsUp.isHydratingForContextId(contextId),
    isRefreshing: store.headsUp.isRefreshingForViewId(smartViewId),
    isRegenerating: store.headsUp.isRegeneratingForViewId(smartViewId),
    isFindingMore: store.headsUp.isFindingMoreForViewId(smartViewId),
    isOperationInFlight: store.headsUp.isBusyForContextId(contextId),
  } as const;

  const errorForContext = store.headsUp.getErrorForContextId(contextId);
  const hasFoundContentBefore = Boolean(view?.has_found_more);

  const pendingHydrationsRef = useRef<Map<string, Promise<Maybe<INoteObservable>>>>(new Map());
  const [loadingNoteIds, setLoadingNoteIds] = useState<Set<string>>(() => new Set());

  const markLoading = useCallback((noteId: string) => {
    setLoadingNoteIds(prev => {
      if (prev.has(noteId)) return prev;
      const next = new Set(prev);
      next.add(noteId);
      return next;
    });
  }, []);

  const markLoaded = useCallback((noteId: string) => {
    setLoadingNoteIds(prev => {
      if (!prev.has(noteId)) return prev;
      const next = new Set(prev);
      next.delete(noteId);
      return next;
    });
  }, []);

  const ensureNoteHydrated = useCallback(
    async (noteId?: string | null): Promise<Maybe<INoteObservable>> => {
      if (!noteId) return undefined;

      const existing = store.notes.getNoteObservableById({ noteId });
      if (existing) {
        markLoaded(noteId);
        return existing;
      }

      let pending = pendingHydrationsRef.current.get(noteId);
      if (!pending) {
        markLoading(noteId);
        pending = (async () => {
          const local = await store.notes.getAsync(noteId);
          if (local) {
            return local;
          }

          const success = await store.notes.fetch(noteId);
          if (!success) {
            return undefined;
          }

          return store.notes.getAsync(noteId);
        })()
          .catch(error => {
            pendingHydrationsRef.current.delete(noteId);
            throw error;
          })
          .finally(() => {
            pendingHydrationsRef.current.delete(noteId);
            markLoaded(noteId);
          });

        pendingHydrationsRef.current.set(noteId, pending);
      }

      return pending;
    },
    [markLoaded, markLoading, store.notes]
  );

  const isNoteLoading = useCallback(
    (noteId: string) => loadingNoteIds.has(noteId),
    [loadingNoteIds]
  );

  const refreshSmartView = useCallback(
    ({ isAuto = false }: { isAuto?: boolean } = {}) => {
      if (!smartViewId) return;

      store.headsUp.refreshSmartView({ smartViewId, contextId }).catch(() => {
        if (isAuto) {
          // No-op: auto refresh failures are silent.
        }
      });
    },
    [contextId, smartViewId, store.headsUp]
  );

  const regenerateSmartView = useCallback(() => {
    if (smartViewId) {
      store.headsUp.regenerateSmartView({ smartViewId, contextId }).catch(() => {});
      return;
    }

    if (!activeNoteId) return;

    store.headsUp
      .generateForContext({ activeNoteId, smartViewKind: SmartViewKind.HeadsUp })
      .catch(() => {});
  }, [store.headsUp, smartViewId, contextId, activeNoteId]);

  const findMore = useCallback(() => {
    if (!smartViewId || hasFoundContentBefore) return;
    store.headsUp.findMoreForSmartView({ smartViewId, contextId }).catch(() => {});
  }, [store.headsUp, smartViewId, contextId, hasFoundContentBefore]);

  const generateForContext = useCallback(() => {
    if (!activeNoteId) return;
    store.headsUp
      .generateForContext({ activeNoteId, smartViewKind: SmartViewKind.HeadsUp })
      .catch(() => {});
  }, [activeNoteId, store.headsUp]);

  const attemptedHydrationContextRef = useRef<string | null>(null);
  const attemptedGenerationContextRef = useRef<string | null>(null);

  useEffect(() => {
    attemptedHydrationContextRef.current = null;
    attemptedGenerationContextRef.current = null;
  }, [contextId]);

  useEffect(() => {
    if (!isDatabaseReady) return;
    if (!contextId) return;
    if (!spaceAccountId) return;
    if (!activeNoteId) return;
    if (viewError) return;
    if (view) return;
    if (isViewLoading) return;
    if (statuses.isOperationInFlight && !statuses.isHydrating) return;
    if (!autoGenerationAllowed) return;
    if (!noteHasPrimaryLabel) return;
    if (attemptedHydrationContextRef.current === contextId) return;

    attemptedHydrationContextRef.current = contextId;
    const invocationContextId = contextId;

    void (async () => {
      const result = await store.headsUp
        .hydrateContext({ activeNoteId, smartViewKind: SmartViewKind.HeadsUp })
        .catch(() => null);

      if (attemptedHydrationContextRef.current !== invocationContextId) {
        return;
      }

      if (result) {
        return;
      }

      if (attemptedGenerationContextRef.current === invocationContextId) {
        return;
      }

      attemptedGenerationContextRef.current = invocationContextId;

      await store.headsUp
        .generateForContext({ activeNoteId, smartViewKind: SmartViewKind.HeadsUp })
        .catch(() => {});
    })();
  }, [
    isDatabaseReady,
    contextId,
    spaceAccountId,
    activeNoteId,
    viewError,
    view,
    isViewLoading,
    statuses.isOperationInFlight,
    statuses.isHydrating,
    store.headsUp,
    autoGenerationAllowed,
    noteHasPrimaryLabel,
  ]);

  return {
    contextRow: context,
    view,
    viewError,
    isViewLoading,
    cards,
    isCardsLoading,
    cardsSignature,
    smartViewId,
    note,
    noteTitle,
    noteHasPrimaryLabel,
    autoGenerationAllowed,
    errorForContext,
    hasFoundContentBefore,
    statuses,
    actions: {
      refreshSmartView,
      regenerateSmartView,
      generateForContext,
      findMore,
      ensureNoteHydrated,
      isNoteLoading,
    },
  };
}
